import { Colors, DropdownMenu, Icon } from '@amelio/ui-library';
import { useState } from 'react';

import { useGetProficiencyLevels } from '@/api/performance-profile';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

type EmployeeLevelMenuProps = {
    competencyId: string;
    levelId: string | null;
    onSelect: (levelId: string) => void;
};

/** The employee's own level on one competency of the profile being assigned to them. */
export const EmployeeLevelMenu = ({ competencyId, levelId, onSelect }: EmployeeLevelMenuProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { data: levels = [] } = useGetProficiencyLevels();
    const [isOpen, setIsOpen] = useState(false);

    const level = levels.find((candidate) => candidate.id === levelId);

    return (
        <div className={'relative shrink-0'}>
            <button
                type={'button'}
                onClick={() => setIsOpen(!isOpen)}
                className={
                    'flex items-center gap-1 rounded-2xl bg-gray-100 px-3 py-1 text-xs font-medium transition-colors ' +
                    (level ? 'text-navy' : 'text-gray-600')
                }
                style={{ backgroundColor: level?.iconColor }}
                data-testid={`roster-selector-proficiency-${competencyId}`}
            >
                {level
                    ? getTextByLanguage(level.label, language)
                    : t('competencies.competencyProfilesAdmin.selector.selectLevel')}
                <Icon
                    name={isOpen ? 'arrow-arrow-up-1' : 'arrow-arrow-down'}
                    size={'xs'}
                />
            </button>

            <DropdownMenu
                open={isOpen}
                onOpenChange={setIsOpen}
                placement={'bottom-right'}
                options={levels.map((candidate) => ({
                    id: candidate.id,
                    value: candidate.id,
                    label: getTextByLanguage(candidate.label, language),
                    selected: candidate.id === levelId,
                    selectedIcon: (
                        <Icon
                            name={'essential-checkmark'}
                            size={'sm'}
                            color={Colors.PRIMARY}
                        />
                    ),
                    selectedIconPosition: 'left' as const,
                }))}
                onOptionClick={(option) => onSelect(option.value as string)}
                dataTestId={`roster-selector-proficiency-dropdown-${competencyId}`}
            />
        </div>
    );
};
